import { Injectable } from '@angular/core'
import { ChartConfiguration } from 'chart.js'
import * as moment from 'moment'
import { HistoryExamItem } from '../../models/patient-exams'

export type AudiometryEar = 'RIGHT' | 'LEFT'
export type AudiometryPathway = 'air' | 'bone'

export interface AudiometryPoint {
	frequency: number
	level: number
	ear: AudiometryEar
	pathway?: AudiometryPathway
	masked?: boolean
	noResponse?: boolean
}

export interface AudiometryInputLeftItem {
	frequency: number
	leftEarVolume?: number | null
	pathway?: AudiometryPathway
	masked?: boolean
	noResponse?: boolean
	heard?: boolean
}

export interface AudiometryInputRightItem {
	frequency: number
	rightEarVolume?: number | null
	pathway?: AudiometryPathway
	masked?: boolean
	noResponse?: boolean
	heard?: boolean
}

export interface AudiometryInput {
	left: AudiometryInputLeftItem[]
	right: AudiometryInputRightItem[]
}

@Injectable({
	providedIn: 'root',
})
export class ExamsChartService {
	constructor() {}

	generateChartTemperature(
		data: HistoryExamItem[]
	): ChartConfiguration['data'] {
		let datasets: any = []
		let labels: string[] = []
		data.forEach((item) => {
			datasets.push(item.examValue.temp)
			labels.push(moment(item.examValue.ts, 'X').format('DD/MM/YYYY'))
		})

		return {
			datasets: [
				{
					data: datasets,
					label: 'Temperatura',
					backgroundColor: 'rgba(255,255,255,.3)',
					borderColor: '#34518F',
					pointBackgroundColor: '#34518F',
					pointBorderColor: '#fff',
					pointHoverBackgroundColor: '#fff',
					pointHoverBorderColor: 'rgba(148,159,177,0.8)',
					fill: 'origin',
				},
			],
			labels,
		}
	}

	generateChartBloodPressure(
		data: HistoryExamItem[]
	): ChartConfiguration['data'] {
		let datasetsSbp: any = []
		let datasetsDbp: any = []
		let labels: string[] = []

		data.forEach((item) => {
			datasetsSbp.push(item.examValue.sbp)
			datasetsDbp.push(item.examValue.dbp)
			labels.push(moment(item.examValue.ts, 'X').format('DD/MM/YYYY'))
		})

		return {
			datasets: [
				{
					data: datasetsSbp,
					label: 'PAS',
					backgroundColor: 'rgba(255,255,255,.3)',
					borderColor: '#34518F',
					pointBackgroundColor: '#34518F',
					pointBorderColor: '#fff',
					pointHoverBackgroundColor: '#fff',
					pointHoverBorderColor: 'rgba(148,159,177,0.8)',
					fill: 'origin',
				},
				{
					data: datasetsDbp,
					label: 'PAD',
					backgroundColor: 'rgba(255,255,255,.3)',
					borderColor: '#EB5757',
					pointBackgroundColor: '#EB5757',
					pointBorderColor: '#fff',
					pointHoverBackgroundColor: '#fff',
					pointHoverBorderColor: 'rgba(148,159,177,0.8)',
					fill: 'origin',
				},
			],
			labels,
		}
	}

	generateChartGlucose(data: HistoryExamItem[]): ChartConfiguration['data'] {
		let datasets: any = []
		let labels: string[] = []
		data.forEach((item) => {
			datasets.push(item.examValue.value)
			labels.push(moment(item.examValue.ts, 'X').format('DD/MM/YYYY'))
		})

		return {
			datasets: [
				{
					data: datasets,
					label: 'Glicemia',
					backgroundColor: 'rgba(255,255,255,.3)',
					borderColor: '#34518F',
					pointBackgroundColor: '#34518F',
					pointBorderColor: '#fff',
					pointHoverBackgroundColor: '#fff',
					pointHoverBorderColor: 'rgba(148,159,177,0.8)',
					fill: 'origin',
				},
			],
			labels,
		}
	}

	generateChartHeartRate(
		data: HistoryExamItem[]
	): ChartConfiguration['data'] {
		let datasets: any = []
		let labels: string[] = []
		data.forEach((item) => {
			datasets.push(item.examValue.hr)
			labels.push(moment(item.examValue.ts, 'X').format('DD/MM/YYYY'))
		})

		return {
			datasets: [
				{
					data: datasets,
					label: 'Frequência cardíaca',
					backgroundColor: 'rgba(255,255,255,.3)',
					borderColor: '#34518F',
					pointBackgroundColor: '#34518F',
					pointBorderColor: '#fff',
					pointHoverBackgroundColor: '#fff',
					pointHoverBorderColor: 'rgba(148,159,177,0.8)',
					fill: 'origin',
				},
			],
			labels,
		}
	}

	generateChartRespiratoryFrequency(
		data: HistoryExamItem[]
	): ChartConfiguration['data'] {
		let datasets: any = []
		let labels: string[] = []
		data.forEach((item) => {
			datasets.push(item.examValue.rr)
			labels.push(moment(item.examValue.ts, 'X').format('DD/MM/YYYY'))
		})

		return {
			datasets: [
				{
					data: datasets,
					label: 'Frequência respiratória',
					backgroundColor: 'rgba(255,255,255,.3)',
					borderColor: '#34518F',
					pointBackgroundColor: '#34518F',
					pointBorderColor: '#fff',
					pointHoverBackgroundColor: '#fff',
					pointHoverBorderColor: 'rgba(148,159,177,0.8)',
					fill: 'origin',
				},
			],
			labels,
		}
	}

	generateChartSpo2(data: HistoryExamItem[]): ChartConfiguration['data'] {
		let datasets: any = []
		let labels: string[] = []
		data.forEach((item) => {
			datasets.push(item.examValue.value)
			labels.push(moment(item.examValue.ts, 'X').format('DD/MM/YYYY'))
		})

		return {
			datasets: [
				{
					data: datasets,
					label: 'Saturação periférica de oxigênio',
					backgroundColor: 'rgba(255,255,255,.3)',
					borderColor: '#34518F',
					pointBackgroundColor: '#34518F',
					pointBorderColor: '#fff',
					pointHoverBackgroundColor: '#fff',
					pointHoverBorderColor: 'rgba(148,159,177,0.8)',
					fill: 'origin',
				},
			],
			labels,
		}
	}

	generateChartAudiometry(data: any): ChartConfiguration['data'] {
		let datasetsLeft: any[] = []
		let datasetsRight: any[] = []
		let labels: number[] = [500, 1000, 1500, 2000, 4000, 8000]

		datasetsLeft = data.left.map((item: any) => {
			return {
				y: item.leftEarVolume,
				x: item.frequency,
			}
		})

		datasetsRight = data.right.map((item: any) => {
			return {
				y: item.rightEarVolume,
				x: item.frequency,
			}
		})

		return {
			datasets: [
				{
					data: datasetsLeft,
					label: 'Esquerdo',
					backgroundColor: 'rgba(255,255,255,.3)',
					borderColor: '#34518F',
					pointBackgroundColor: '#34518F',
					pointBorderColor: '#fff',
					pointHoverBackgroundColor: '#fff',
					pointHoverBorderColor: 'rgba(148,159,177,0.8)',
					fill: 'origin',
					showLine: true,
				},
				{
					data: datasetsRight,
					label: 'Direito',
					backgroundColor: 'rgba(255,255,255,.3)',
					borderColor: '#EB5757',
					pointBackgroundColor: '#EB5757',
					pointBorderColor: '#fff',
					pointHoverBackgroundColor: '#fff',
					pointHoverBorderColor: 'rgba(148,159,177,0.8)',
					fill: 'origin',
					showLine: true,
				},
			],
			labels,
		}
	}

	private static makeSymbolCanvas(
		symbol: string,
		color: string,
		withDownArrow = false
	): HTMLCanvasElement {
		const size = withDownArrow ? 32 : 28
		const canvas = document.createElement('canvas')
		canvas.width = size
		canvas.height = size
		const ctx = canvas.getContext('2d')!
		ctx.lineWidth = 2
		const mid = size / 2
		const r = size * 0.35
		const yShift = withDownArrow ? 3 : 0

		ctx.fillStyle = 'transparent'
		ctx.beginPath()
		ctx.arc(mid, mid - yShift, r + 3, 0, Math.PI * 2)
		ctx.fill()

		ctx.strokeStyle = color
		ctx.beginPath()
		switch (symbol) {
			case 'O':
				ctx.arc(mid, mid - yShift, r, 0, Math.PI * 2)
				break
			case 'X':
				ctx.moveTo(mid - r, mid - r - yShift)
				ctx.lineTo(mid + r, mid + r - yShift)
				ctx.moveTo(mid + r, mid - r - yShift)
				ctx.lineTo(mid - r, mid + r - yShift)
				break
		}
		ctx.stroke()

		if (withDownArrow) {
			const top = mid + r
			const bottom = size + 1
			ctx.beginPath()
			ctx.moveTo(mid, top)
			ctx.lineTo(mid, bottom - 6)
			ctx.stroke()
			ctx.beginPath()
			ctx.moveTo(mid - 5, bottom - 8)
			ctx.lineTo(mid, bottom - 2)
			ctx.lineTo(mid + 5, bottom - 8)
			ctx.stroke()
		}

		return canvas
	}

	private symbolForPointAir(p: AudiometryPoint): string {
		const masked = !!p.masked
		if (p.ear === 'RIGHT') return masked ? '△' : 'O'
		return masked ? '▢' : 'X'
	}

	private colorForPoint(p: AudiometryPoint): string {
		return p.ear === 'RIGHT' ? '#EB5757' : '#34518F'
	}

	private static symbolRadiusPx(nr: boolean): number {
		return Math.round((nr ? 32 : 28) * 0.35 + 3)
	}

	// ===========================
	// Normalização
	// ===========================

	private normalizeAudiometryInput(data: AudiometryInput): {
		left: AudiometryPoint[]
		right: AudiometryPoint[]
	} {
		const normalizeSide = (
			values: any[],
			ear: 'LEFT' | 'RIGHT',
			volumeKey: 'leftEarVolume' | 'rightEarVolume'
		): AudiometryPoint[] =>
			(values || [])
				.map((v) => {
					const level = v[volumeKey] ?? undefined
					const heard = v.heard
					const nr =
						v.noResponse ||
						heard === false ||
						level === null ||
						level === undefined ||
						Number.isNaN(level as any)
					return {
						frequency: v.frequency,
						level: nr ? 120 : (level as number),
						ear,
						pathway: 'air' as const,
						masked: !!v.masked,
						noResponse: nr,
					}
				})
				.sort((a, b) => a.frequency - b.frequency)

		return {
			left: normalizeSide(data.left, 'LEFT', 'leftEarVolume'),
			right: normalizeSide(data.right, 'RIGHT', 'rightEarVolume'),
		}
	}

	// ===========================
	// Data + Datasets
	// ===========================

	generateChartAudiometryBR(
		data: AudiometryInput
	): ChartConfiguration['data'] {
		const normalized = this.normalizeAudiometryInput(data)

		// frequências fixas (ordem)
		const baseFreqs = [
			125, 250, 500, 750, 1000, 1500, 2000, 3000, 4000, 6000, 8000,
		]
		const labels = baseFreqs.map(String)

		const mapPoints = (points: AudiometryPoint[]) =>
			points.map((p) => ({
				x: String(p.frequency), // agora string p/ category scale
				y: p.level,
				_meta: p,
				symbol: this.symbolForPointAir(p),
				color: this.colorForPoint(p),
				nr: !!p.noResponse,
			})) as any[]

		const leftPoints = mapPoints(normalized.left)
		const rightPoints = mapPoints(normalized.right)

		const makeDataset = (
			label: string,
			points: any[],
			color: string,
			defaultSymbol: string
		) => ({
			label,
			data: points,
			type: 'scatter' as const,
			showLine: true,
			borderColor: color,
			backgroundColor: color,
			pointRadius: (ctx: any) =>
				ExamsChartService.symbolRadiusPx(!!ctx.raw?.nr),
			pointHoverRadius: (ctx: any) =>
				ExamsChartService.symbolRadiusPx(!!ctx.raw?.nr) + 2,
			pointHitRadius: (ctx: any) =>
				ExamsChartService.symbolRadiusPx(!!ctx.raw?.nr) + 2,
			pointStyle: (ctx: any) => {
				const sym = ctx.raw?.symbol ?? defaultSymbol
				const c = ctx.raw?.color ?? color
				const nr = !!ctx.raw?.nr
				return ExamsChartService.makeSymbolCanvas(sym, c, nr)
			},
			pointBorderColor: (ctx: any) => ctx.raw?.color ?? color,
			pointBackgroundColor: '#ffffff',
			tension: 0,
			hoverBorderWidth: 2,
		})

		return {
			labels, // labels do eixo category
			datasets: [
				makeDataset('Esquerda', leftPoints, '#34518F', 'X'),
				makeDataset('Direita', rightPoints, '#EB5757', 'O'),
			],
		}
	}

	// ===========================
	// Options
	// ===========================

	getAudiometryBROptions(): ChartConfiguration['options'] {
		return {
			responsive: true,
			maintainAspectRatio: false,
			parsing: false,
			interaction: {
				mode: 'nearest',
				intersect: true,
				axis: 'xy',
			},
			layout: { padding: { top: 8, right: 16, bottom: 12, left: 16 } },
			elements: {
				point: { borderWidth: 2 },
			},
			scales: {
				x: {
					type: 'category',
					title: { display: true, text: 'Frequência (Hz)' },
					offset: true,
					ticks: { padding: 8 },
				},
				y: {
					title: { display: true, text: 'dB HL' },
					reverse: true,
					min: -10,
					max: 150,
					ticks: { stepSize: 10, padding: 8 },
				},
			},
			plugins: {
				legend: { position: 'top' },
				tooltip: {
					position: 'nearest',
					intersect: true,
					callbacks: {
						label: (ctx: any) => {
							const p = ctx.raw?._meta as AudiometryPoint
							const sym = ctx.raw?.symbol ?? ''
							const ear =
								p?.ear === 'RIGHT' ? 'Direita' : 'Esquerda'
							const mask = p?.masked ? ' (masc.)' : ''
							const nr = p?.noResponse ? ' (NR)' : ''
							return `${ear} Aérea${mask}${nr}: ${ctx.parsed.y} dB @ ${ctx.parsed.x} Hz ${sym}`
						},
					},
				},
			},
		}
	}
}
